<template>
  <div>
    <button
      ref="toggleButton"
      :class="['btn btn-light btn-sm', { 'dropdown-toggle': showDropdownCaret }, 'text-truncate filter']"
      :title="filter.selectedTitle"
      aria-expanded="false"
      aria-haspopup="true"
      data-bs-toggle="dropdown"
      type="button"
    >
      <slot><span v-html="filter.title"/>: {{ filter.selectedTitle }}</slot>
    </button>
    <div
      class="dropdown-menu filter list"
    >
      <template
        v-for="(item, itemIdx) in filter.listItems"
        :key="itemIdx"
      >
        <div
          v-if="item.type == 'separator'"
          class="dropdown-divider"
        />
        <a
          v-else
          :title="item.title"
          class="dropdown-item text-truncate"
          href="#"
          @click.prevent="selectItem($event, item)"
        >
          <span
            :class="{ 'glyphicons checkmark': true, invisible: !isSelected(item) }"
          />
          {{ item.title }}
        </a>
      </template>
      <div
        v-if="filter.multiple"
        class="apply-wrap"
      >
        <hr>
        <button
          class="btn btn-sm btn-primary"
          @click="applyFilter"
        >
          Apply
        </button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {Dropdown} from 'bootstrap'
import type {GridFilter, GridFilterListItem} from './types'
import {onBeforeUnmount, onMounted, ref, useTemplateRef, watch} from "vue";
import {isNonEmptyString} from "@/js/js-utils";

const getSelectedValues = function (newValue: string | string[]): string[] {
  let result: string[]

  if (Array.isArray(newValue)) {
    result = newValue.slice()
  } else {
    result = [newValue]
  }
  if (result.length == 0) {
    result = ['']
  }
  return result
}

const model = defineModel<string[]>({required: true})

const props = defineProps({
  filter: {type: Object as () => GridFilter, required: true},
  showDropdownCaret: {type: Boolean, default: true},
})

const toggleButtonRef = useTemplateRef<HTMLButtonElement>("toggleButton")

const selectedValues = ref<string[]>(getSelectedValues(model.value))

watch(
  () => model.value,
  (newValue) => {
    selectedValues.value = getSelectedValues(newValue)
  }
)

onMounted(() => {
  const toggleEl = toggleButtonRef.value
  if (!toggleEl) {
    return
  }

  // keep the menu below the button, don't flip it up
  Dropdown.getOrCreateInstance(toggleEl, {
    popperConfig: (defaultConfig) => ({
      ...defaultConfig,
      modifiers: [...defaultConfig.modifiers, {name: 'flip', enabled: false}],
    }),
  })

  toggleEl.addEventListener('show.bs.dropdown', () => {
    // reset selected values
    selectedValues.value = getSelectedValues(model.value)
  })
})

onBeforeUnmount(() => {
  if (toggleButtonRef.value) {
    Dropdown.getInstance(toggleButtonRef.value)?.dispose()
  }
})

const selectItem = (e: Event, item: GridFilterListItem): void => {
  if (props.filter.multiple) {
    if (isNonEmptyString(item.value)) {
      e.stopPropagation()

      const elIdx = selectedValues.value.indexOf(item.value)
      if (elIdx == -1) {
        if (isNonEmptyString(item.value)) {
          // remove empty value, if it's in the list of selected
          selectedValues.value = selectedValues.value.filter((value) => isNonEmptyString(value))
        }
        selectedValues.value.push(item.value)
      } else {
        selectedValues.value.splice(elIdx, 1)
        if (selectedValues.value.length == 0) {
          selectedValues.value = ['']
        }
      }
    } else {
      // 'Any' item selected, clear all values
      selectedValues.value = ['']
      applyFilter()
    }
  } else {
    selectedValues.value = [item.value]
    applyFilter()
  }
}

const applyFilter = (): void => {
  model.value = selectedValues.value
}

const isSelected = (item: GridFilterListItem): boolean => {
  return selectedValues.value.indexOf(item.value) != -1
}
</script>
